import { isConfirmationDateValueValid } from '../../custom-fields/utils/confirmation-date.util';
import { isItemListValueValid } from '../../custom-fields/utils/item-list.util';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../core';
import { TenantService } from '../../../core/tenant/tenant.service';
import { OperationsApiService } from '../../../core/api';
import { CompanyUsersService } from '../../../core/services/company-users.service';
import { CustomFieldsDataService } from '../../custom-fields/services/custom-fields-data.service';
import { OnboardingChecklistStore } from '../../onboarding/stores/onboarding-checklist.store';
import { mapProjectContractToUiModel } from '../../projects/adapters/project.adapter';
import { ProjectsStore } from '../../projects/stores/projects.store';
import { mapWorkflowContractToUiModel } from '../../workflows/adapters/workflow.adapter';
import { WorkflowsStore } from '../../workflows/stores/workflows.store';
import type {
  CustomFieldRawValue,
  CustomFieldType,
  CustomFieldValueViewModel,
  CustomFieldViewModel,
} from '../../custom-fields/models/custom-field.model';
import {
  parseFileFieldValueToAttachments,
  type FileFieldAttachment,
} from '../../../shared/utils';
import { normalizeBrazilPhoneForSubmit } from '../../../shared/utils/brazil-phone.util';
import {
  formatNumberFieldValueForApi,
  parseFlexibleNumberString,
  resolveNumberFieldDecimalPlaces,
  shouldPreserveLeadingZeroIntegerRaw,
} from '../../custom-fields/utils/number-field.util';
import {
  mapActivityToViewModel,
  mapAttachmentToViewModel,
  mapChecklistToViewModel,
  mapCommentToViewModel,
  resolveStageName,
  toFieldValueMap,
} from '../adapters/card-details.adapter';
import {
  CardActivitiesApiService,
  CardAttachmentsApiService,
  CardChecklistItemsApiService,
  CardChecklistsApiService,
  CardCommentsApiService,
  CardDetailsApiService,
  CardFieldValuesApiService,
} from '../api';
import { WorkflowItemsService } from '../../workflows/services/workflow-items.mock.service';
import {
  WorkflowCardFieldDisplayOrderApiService,
  type WorkflowCardFieldDisplayOrderItem,
} from '../../workflows/api/workflow-card-field-display-order-api.service';
import { sortCustomFieldViewModelsByDisplayOrder } from '../../workflows/utils/workflow-card-field-display-sort.util';
import type {
  CardActivityViewModel,
  CardAttachmentViewModel,
  CardChecklistViewModel,
  CardCommentViewModel,
  CardDetailsViewModel,
} from '../models/card-details.model';
import type {
  CardAttachmentUploadResultModel,
  CardChecklistItemModel,
  CardFieldValueModel,
  CardOwnerOptionModel,
  UpsertCardFieldValueRequestModel,
} from '../models/card-details-api.models';

@Injectable({ providedIn: 'root' })
export class CardDetailsStore {
  /** Move: `targetPosition` ≥ 1; valor alto → API encaixa no fim da coluna (clamp). */
  private static readonly MOVE_APPEND_END = 1_000_000;

  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);
  private readonly operationsApi = inject(OperationsApiService);
  private readonly cardApi = inject(CardDetailsApiService);
  private readonly checklistsApi = inject(CardChecklistsApiService);
  private readonly checklistItemsApi = inject(CardChecklistItemsApiService);
  private readonly commentsApi = inject(CardCommentsApiService);
  private readonly attachmentsApi = inject(CardAttachmentsApiService);
  private readonly activitiesApi = inject(CardActivitiesApiService);
  private readonly fieldValuesApi = inject(CardFieldValuesApiService);
  private readonly customFieldsData = inject(CustomFieldsDataService);
  private readonly companyUsers = inject(CompanyUsersService);
  private readonly projectsStore = inject(ProjectsStore);
  private readonly workflowsStore = inject(WorkflowsStore);
  private readonly workflowItems = inject(WorkflowItemsService);
  private readonly cardFieldDisplayOrderApi = inject(WorkflowCardFieldDisplayOrderApiService);
  private readonly checklistStore = inject(OnboardingChecklistStore);

  private readonly cardIdSignal = signal<string | null>(null);
  private readonly cardSignal = signal<CardDetailsViewModel | null>(null);
  private readonly checklistsSignal = signal<CardChecklistViewModel[]>([]);
  private readonly commentsSignal = signal<CardCommentViewModel[]>([]);
  private readonly attachmentsSignal = signal<CardAttachmentViewModel[]>([]);
  private readonly activitiesSignal = signal<CardActivityViewModel[]>([]);
  private readonly fieldValuesSignal = signal<CardFieldValueModel[]>([]);

  private readonly loadingCardSignal = signal(false);
  private readonly loadingChecklistsSignal = signal(false);
  private readonly loadingCommentsSignal = signal(false);
  private readonly loadingAttachmentsSignal = signal(false);
  private readonly loadingActivitiesSignal = signal(false);
  private readonly loadingFieldValuesSignal = signal(false);

  private readonly savingCardSignal = signal(false);
  private readonly mutatingChecklistsSignal = signal(false);
  private readonly mutatingCommentsSignal = signal(false);
  private readonly uploadingAttachmentSignal = signal(false);
  private readonly mutatingFieldValuesSignal = signal(false);

  private readonly errorCardSignal = signal<string | null>(null);
  private readonly lastSaveErrorSignal = signal<string | null>(null);
  private readonly errorChecklistsSignal = signal<string | null>(null);
  private readonly errorCommentsSignal = signal<string | null>(null);
  private readonly errorAttachmentsSignal = signal<string | null>(null);
  private readonly errorActivitiesSignal = signal<string | null>(null);
  private readonly errorFieldValuesSignal = signal<string | null>(null);

  readonly currentCard = this.cardSignal.asReadonly();
  readonly checklists = this.checklistsSignal.asReadonly();
  readonly comments = this.commentsSignal.asReadonly();
  readonly attachments = this.attachmentsSignal.asReadonly();
  readonly activities = this.activitiesSignal.asReadonly();

  readonly loadingCard = this.loadingCardSignal.asReadonly();
  readonly loadingChecklists = this.loadingChecklistsSignal.asReadonly();
  readonly loadingComments = this.loadingCommentsSignal.asReadonly();
  readonly loadingAttachments = this.loadingAttachmentsSignal.asReadonly();
  readonly loadingActivities = this.loadingActivitiesSignal.asReadonly();
  readonly loadingFieldValues = this.loadingFieldValuesSignal.asReadonly();

  readonly savingCard = this.savingCardSignal.asReadonly();
  readonly mutatingChecklists = this.mutatingChecklistsSignal.asReadonly();
  readonly mutatingComments = this.mutatingCommentsSignal.asReadonly();
  readonly uploadingAttachment = this.uploadingAttachmentSignal.asReadonly();
  readonly mutatingFieldValues = this.mutatingFieldValuesSignal.asReadonly();

  readonly errorCard = this.errorCardSignal.asReadonly();
  readonly lastSaveError = this.lastSaveErrorSignal.asReadonly();
  readonly errorChecklists = this.errorChecklistsSignal.asReadonly();
  readonly errorComments = this.errorCommentsSignal.asReadonly();
  readonly errorAttachments = this.errorAttachmentsSignal.asReadonly();
  readonly errorActivities = this.errorActivitiesSignal.asReadonly();
  readonly errorFieldValues = this.errorFieldValuesSignal.asReadonly();

  readonly customFields = computed<CustomFieldViewModel[]>(() => this.currentCard()?.customFields ?? []);
  readonly customFieldValues = computed<CustomFieldValueViewModel[]>(() => {
    const map = toFieldValueMap(this.fieldValuesSignal());
    const normalizedMap = new Map<string, CustomFieldRawValue>();
    for (const [fieldId, value] of map) {
      const normalized = this.normalizeFieldId(fieldId);
      if (normalized) normalizedMap.set(normalized, value);
    }

    return this.customFields().map((field) => ({
      fieldId: field.fieldId,
      value: map.get(field.fieldId) ?? normalizedMap.get(this.normalizeFieldId(field.fieldId)) ?? null,
    }));
  });
  readonly checklistProgress = computed(() => {
    const items = this.checklists().flatMap((checklist) => checklist.items ?? []);
    const total = items.length;
    const done = items.filter((item) => item.isCompleted).length;
    return { done, total };
  });
  readonly sortedChecklists = computed(() =>
    [...this.checklists()].sort((a, b) => a.order - b.order)
  );
  readonly sortedComments = computed(() =>
    [...this.comments()].sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
  );
  readonly sortedAttachments = computed(() =>
    [...this.attachments()].sort((a, b) => (a.uploadedAt > b.uploadedAt ? -1 : 1))
  );
  readonly sortedActivities = computed(() =>
    [...this.activities()].sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
  );
  readonly canShowChecklistItemsFully = computed(() =>
    this.checklists().some((checklist) => checklist.items.length > 0)
  );
  readonly isLoadingAny = computed(
    () =>
      this.loadingCard() ||
      this.loadingChecklists() ||
      this.loadingComments() ||
      this.loadingAttachments() ||
      this.loadingActivities() ||
      this.loadingFieldValues()
  );

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.reset();
      }
    });
  }

  loadCard(cardId: string | null): void {
    if (!cardId) {
      this.cardIdSignal.set(null);
      this.cardSignal.set(null);
      this.errorCardSignal.set(this.i18n.t('cards.details.errorInvalidCard'));
      return;
    }

    this.cardIdSignal.set(cardId);
    this.loadingCardSignal.set(true);
    this.errorCardSignal.set(null);

    void this.loadCardBase(cardId)
      .then((opts) => {
        const parallel: Promise<unknown>[] = [
          this.reloadChecklists(),
          this.reloadComments(),
          this.reloadAttachments(),
          this.reloadActivities(),
        ];
        if (!opts.skippedFieldValuesReload) {
          parallel.push(this.reloadFieldValues());
        }
        void Promise.allSettled(parallel);
      })
      .finally(() => this.loadingCardSignal.set(false));
  }

  async updateTitle(title: string): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    const trimmed = title.trim();
    if (!trimmed) return false;
    return this.updateCard({
      title: trimmed,
      description: card.description,
      workflowId: card.workflowId,
      workflowStageId: card.workflowStageId,
      dueDate: card.dueDate,
      ownerUserId: card.ownerUserId,
      isCompleted: card.isCompleted,
    });
  }

  async updateDescription(description: string): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    return this.updateCard({
      title: card.title,
      description: description.trim() || null,
      workflowId: card.workflowId,
      workflowStageId: card.workflowStageId,
      dueDate: card.dueDate,
      ownerUserId: card.ownerUserId,
      isCompleted: card.isCompleted,
    });
  }

  async updateOwner(ownerUserId: string | null): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    this.savingCardSignal.set(true);
    this.lastSaveErrorSignal.set(null);
    try {
      await this.cardApi.updateOwner(card.id, { ownerUserId });
      const uid = ownerUserId?.trim() || null;
      const ownerName = !uid
        ? this.i18n.t('common.unassigned')
        : (card.ownerOptions.find((o) => o.id === uid)?.name ?? card.ownerName);
      this.cardSignal.update((c) =>
        c
          ? {
              ...c,
              ownerUserId: uid,
              ownerName,
            }
          : c
      );
      try {
        await this.workflowItems.refreshCardFromApi(card.id);
      } catch {
        /* best effort — lista atualiza em próximo reload */
      }
      return true;
    } catch (error) {
      this.lastSaveErrorSignal.set(this.resolveApiError(error, this.i18n.t('cards.form.errorSave')));
      return false;
    } finally {
      this.savingCardSignal.set(false);
    }
  }

  async updateDueDate(dueDate: string | null): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    this.savingCardSignal.set(true);
    this.lastSaveErrorSignal.set(null);
    try {
      await this.cardApi.updateDueDate(card.id, { dueDate });
      await this.loadCardBase(card.id);
      try {
        await this.workflowItems.refreshCardFromApi(card.id);
      } catch {
        /* best effort — lista atualiza em próximo reload */
      }
      return true;
    } catch (error) {
      this.lastSaveErrorSignal.set(this.resolveApiError(error, this.i18n.t('cards.form.errorSave')));
      return false;
    } finally {
      this.savingCardSignal.set(false);
    }
  }

  async updateCompletion(isCompleted: boolean): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    this.savingCardSignal.set(true);
    this.lastSaveErrorSignal.set(null);
    try {
      await this.cardApi.updateCompletion(card.id, { isCompleted });
      await this.loadCardBase(card.id);
      try {
        await this.workflowItems.refreshCardFromApi(card.id);
      } catch {
        /* best effort — lista atualiza em próximo reload */
      }
      return true;
    } catch (error) {
      this.lastSaveErrorSignal.set(this.resolveApiError(error, this.i18n.t('cards.form.errorSave')));
      return false;
    } finally {
      this.savingCardSignal.set(false);
    }
  }

  async updateWorkflowStage(workflowStageId: string): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    this.savingCardSignal.set(true);
    this.lastSaveErrorSignal.set(null);
    try {
      await this.cardApi.moveCard(card.id, {
        targetWorkflowStageId: workflowStageId,
        targetPosition: CardDetailsStore.MOVE_APPEND_END,
      });
      const wfId = card.workflowId;
      if (wfId) await this.workflowItems.reloadWorkflowCards(wfId);
      await this.loadCardBase(card.id);
      return true;
    } catch (error) {
      this.lastSaveErrorSignal.set(this.resolveApiError(error, this.i18n.t('cards.form.errorSave')));
      return false;
    } finally {
      this.savingCardSignal.set(false);
    }
  }

  async deleteCard(): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    const wfId = card.workflowId;
    this.savingCardSignal.set(true);
    this.lastSaveErrorSignal.set(null);
    try {
      await this.cardApi.deleteCard(card.id);
      if (wfId) await this.workflowItems.reloadWorkflowCards(wfId);
      this.cardSignal.set(null);
      return true;
    } catch (error) {
      this.lastSaveErrorSignal.set(this.resolveApiError(error, this.i18n.t('cards.form.errorSave')));
      return false;
    } finally {
      this.savingCardSignal.set(false);
    }
  }

  async createChecklist(title: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      const nextOrder = Math.max(1, this.checklists().length + 1);
      await this.checklistsApi.create(cardId, { cardId, title, order: nextOrder });
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async updateChecklist(checklistId: string, title: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      const existing = this.checklists().find((item) => item.id === checklistId);
      const order = existing?.order && existing.order > 0 ? existing.order : 1;
      await this.checklistsApi.update(cardId, checklistId, {
        id: checklistId,
        cardId,
        title,
        order,
      });
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async deleteChecklist(checklistId: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      await this.checklistsApi.delete(cardId, checklistId);
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async reorderChecklists(orderedChecklistIds: string[]): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      await this.checklistsApi.reorder(cardId, {
        cardId,
        items: orderedChecklistIds.map((checklistId, index) => ({
          checklistId,
          order: index + 1,
        })),
      });
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async createChecklistItem(checklistId: string, title: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      const checklist = this.checklists().find((item) => item.id === checklistId);
      const nextOrder = Math.max(1, (checklist?.items.length ?? 0) + 1);
      const created = await this.checklistItemsApi.create(cardId, checklistId, {
        cardId,
        cardChecklistId: checklistId,
        title,
        order: nextOrder,
      });
      if (!created?.id) return false;
      this.upsertChecklistItemLocal(checklistId, {
        ...created,
        title: created.title || title,
        order: created.order > 0 ? created.order : nextOrder,
      });
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async toggleChecklistItem(checklistId: string, itemId: string, isCompleted: boolean): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      const toggled = await this.checklistItemsApi.toggle(cardId, checklistId, itemId, {
        id: itemId,
        cardId,
        cardChecklistId: checklistId,
        isCompleted,
      });
      this.upsertChecklistItemLocal(checklistId, {
        id: itemId,
        cardChecklistId: checklistId,
        title: toggled?.title ?? '',
        isCompleted: toggled?.isCompleted ?? isCompleted,
        order: toggled?.order ?? 0,
        completedAt: toggled?.completedAt ?? null,
        completedByUserId: toggled?.completedByUserId ?? null,
      });
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async deleteChecklistItem(checklistId: string, itemId: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      await this.checklistItemsApi.delete(cardId, checklistId, itemId);
      this.removeChecklistItemLocal(checklistId, itemId);
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async createPrimaryChecklistItem(title: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      let checklist = [...this.checklists()].sort((a, b) => a.order - b.order)[0];
      if (!checklist) {
        const createdChecklist = await this.checklistsApi.create(cardId, {
          cardId,
          title: this.i18n.t('cards.checklist.title', undefined, 'Checklist'),
          order: 1,
        });
        if (createdChecklist?.id) {
          checklist = mapChecklistToViewModel(createdChecklist);
          this.checklistsSignal.update((current) => {
            const exists = current.some((item) => item.id === checklist!.id);
            return exists ? current : [...current, checklist!];
          });
        } else {
          await this.reloadChecklists();
          checklist = [...this.checklists()].sort((a, b) => a.order - b.order)[0];
        }
      }
      if (!checklist) return false;

      const nextOrder = Math.max(1, (checklist.items?.length ?? 0) + 1);
      const created = await this.checklistItemsApi.create(cardId, checklist.id, {
        cardId,
        cardChecklistId: checklist.id,
        title,
        order: nextOrder,
      });
      if (!created?.id) return false;
      this.upsertChecklistItemLocal(checklist.id, {
        ...created,
        title: created.title || title,
        order: created.order > 0 ? created.order : nextOrder,
      });
      await this.reloadChecklists();
      return true;
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingChecklistsSignal.set(false);
    }
  }

  async createComment(content: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingCommentsSignal.set(true);
    this.errorCommentsSignal.set(null);
    try {
      await this.commentsApi.create(cardId, { content });
      await this.reloadComments();
      await this.reloadActivities();
      void this.checklistStore.refresh();
      return true;
    } catch (error) {
      this.errorCommentsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingCommentsSignal.set(false);
    }
  }

  async updateComment(commentId: string, content: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingCommentsSignal.set(true);
    this.errorCommentsSignal.set(null);
    try {
      await this.commentsApi.update(cardId, commentId, { content });
      await this.reloadComments();
      await this.reloadActivities();
      return true;
    } catch (error) {
      this.errorCommentsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingCommentsSignal.set(false);
    }
  }

  async deleteComment(commentId: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingCommentsSignal.set(true);
    this.errorCommentsSignal.set(null);
    try {
      await this.commentsApi.delete(cardId, commentId);
      await this.reloadComments();
      await this.reloadActivities();
      return true;
    } catch (error) {
      this.errorCommentsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingCommentsSignal.set(false);
    }
  }

  async uploadAttachment(file: File): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.uploadingAttachmentSignal.set(true);
    this.errorAttachmentsSignal.set(null);
    try {
      await this.attachmentsApi.upload(cardId, file);
      await this.reloadAttachments();
      await this.reloadActivities();
      return true;
    } catch (error) {
      this.errorAttachmentsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.uploadingAttachmentSignal.set(false);
    }
  }

  async deleteAttachment(attachmentId: string): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.uploadingAttachmentSignal.set(true);
    this.errorAttachmentsSignal.set(null);
    try {
      await this.attachmentsApi.delete(cardId, attachmentId);
      await this.reloadAttachments();
      await this.reloadActivities();
      return true;
    } catch (error) {
      this.errorAttachmentsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.uploadingAttachmentSignal.set(false);
    }
  }

  async upsertFieldValue(
    fieldId: string,
    value: CustomFieldRawValue,
    options?: { skipReload?: boolean }
  ): Promise<boolean> {
    const cardId = this.cardIdSignal();
    if (!cardId) return false;
    this.mutatingFieldValuesSignal.set(true);
    this.errorFieldValuesSignal.set(null);
    try {
      const fieldType = this.customFields().find((field) => field.fieldId === fieldId)?.type;

      if (fieldType === 'ITEM_LIST' && !isItemListValueValid(value,
        this.customFields().find(field => field.fieldId === fieldId)?.required)) {
        this.errorFieldValuesSignal.set(this.i18n.t('itemList.invalid'));
        return false;
      }

      if (fieldType === 'CONFIRMATION_DATE' && !isConfirmationDateValueValid(value)) {
        this.errorFieldValuesSignal.set(this.i18n.t('confirmationdate.daterequired'));
        return false;
      }
      if (fieldType === 'FILE') {
        return await this.handleFileFieldUpsert(cardId, fieldId, value);
      }

      const existing = this.fieldValuesSignal().some((item) => item.workflowFieldId === fieldId);
      const payloadValue = await this.toFieldPersistedValue(cardId, fieldId, fieldType, value);
      const payload = { cardId, workflowFieldId: fieldId, value: payloadValue };
      if (payloadValue == null || (typeof payloadValue === 'string' && !payloadValue.trim())) {
        await this.fieldValuesApi.delete(cardId, fieldId);
        this.applyLocalFieldValue(fieldId, null);
        this.workflowItems.patchItemCustomFieldLocal(cardId, fieldId, null);
      } else {
        await this.upsertFieldValueWithFallback(existing, payload);
        this.applyLocalFieldValue(fieldId, payloadValue);
        this.workflowItems.patchItemCustomFieldLocal(cardId, fieldId, payloadValue);
      }
      if (!options?.skipReload) {
        try {
          await this.reloadFieldValues();
        } catch {
          /* best effort */
        }
      }
      try {
        await this.workflowItems.refreshCardFromApi(cardId);
      } catch {
        /* lista/board: best effort */
      }
      return true;
    } catch (error) {
      this.errorFieldValuesSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.mutatingFieldValuesSignal.set(false);
    }
  }

  private async handleFileFieldUpsert(
    cardId: string,
    fieldId: string,
    value: CustomFieldRawValue
  ): Promise<boolean> {
    const isFileUpload =
      value instanceof File ||
      (Array.isArray(value) && value.length > 0 && value.every((v) => v instanceof File));

    if (isFileUpload) {
      const files: File[] = value instanceof File ? [value] : (value as File[]);
      for (const file of files) {
        await this.attachmentsApi.upload(cardId, file);
      }
      try {
        await this.reloadAttachments();
      } catch { /* best effort */ }
      try {
        await this.workflowItems.refreshCardFromApi(cardId);
      } catch { /* best effort */ }
      return true;
    }

    if (value == null || (typeof value === 'string' && !value.trim())) {
      try {
        await this.fieldValuesApi.delete(cardId, fieldId);
      } catch { /* best effort */ }
      this.applyLocalFieldValue(fieldId, null);
      this.workflowItems.patchItemCustomFieldLocal(cardId, fieldId, null);
      return true;
    }

    if (typeof value === 'string') {
      this.applyLocalFieldValue(fieldId, value);
      this.workflowItems.patchItemCustomFieldLocal(cardId, fieldId, value);
    }
    return true;
  }

  private async upsertFieldValueWithFallback(
    existing: boolean,
    payload: UpsertCardFieldValueRequestModel
  ): Promise<void> {
    if (existing) {
      try {
        await this.fieldValuesApi.update(payload);
        return;
      } catch (error) {
        if (!this.isRecoverableUpsertError(error)) throw error;
      }
      await this.fieldValuesApi.create(payload);
      return;
    }

    try {
      await this.fieldValuesApi.create(payload);
      return;
    } catch (error) {
      if (!this.isRecoverableUpsertError(error)) throw error;
    }
    await this.fieldValuesApi.update(payload);
  }

  private isRecoverableUpsertError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    return error.status === 400 || error.status === 404 || error.status === 409;
  }

  retry(cardId: string | null): void {
    this.loadCard(cardId);
  }

  reset(): void {
    this.cardIdSignal.set(null);
    this.cardSignal.set(null);
    this.checklistsSignal.set([]);
    this.commentsSignal.set([]);
    this.attachmentsSignal.set([]);
    this.activitiesSignal.set([]);
    this.fieldValuesSignal.set([]);
    this.loadingCardSignal.set(false);
    this.loadingChecklistsSignal.set(false);
    this.loadingCommentsSignal.set(false);
    this.loadingAttachmentsSignal.set(false);
    this.loadingActivitiesSignal.set(false);
    this.loadingFieldValuesSignal.set(false);
    this.savingCardSignal.set(false);
    this.mutatingChecklistsSignal.set(false);
    this.mutatingCommentsSignal.set(false);
    this.uploadingAttachmentSignal.set(false);
    this.mutatingFieldValuesSignal.set(false);
    this.errorCardSignal.set(null);
    this.errorChecklistsSignal.set(null);
    this.errorCommentsSignal.set(null);
    this.errorAttachmentsSignal.set(null);
    this.errorActivitiesSignal.set(null);
    this.errorFieldValuesSignal.set(null);
  }

  private async updateCard(payload: {
    title: string;
    description: string | null;
    workflowId: string;
    workflowStageId: string;
    dueDate: string | null;
    ownerUserId: string | null;
    isCompleted: boolean;
  }): Promise<boolean> {
    const card = this.currentCard();
    if (!card) return false;
    this.savingCardSignal.set(true);
    this.errorCardSignal.set(null);
    try {
      await this.cardApi.updateCard(card.id, payload);
      this.applySavedCardShell(card.id, payload);
      await this.loadCardBase(card.id);
      try {
        await this.workflowItems.refreshCardFromApi(card.id);
      } catch {
        /* best effort — lista atualiza em próximo reload */
      }
      return true;
    } catch (error) {
      this.errorCardSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return false;
    } finally {
      this.savingCardSignal.set(false);
    }
  }

  private applySavedCardShell(
    cardId: string,
    payload: {
      title: string;
      description: string | null;
      dueDate: string | null;
      ownerUserId: string | null;
      isCompleted: boolean;
    }
  ): void {
    this.cardSignal.update((current) =>
      current?.id === cardId
        ? {
            ...current,
            title: payload.title,
            description: payload.description,
            dueDate: payload.dueDate,
            ownerUserId: payload.ownerUserId,
            isCompleted: payload.isCompleted,
          }
        : current
    );
    this.workflowItems.patchItemLocal(cardId, {
      title: payload.title,
      description: payload.description ?? undefined,
      dueDate: payload.dueDate,
    });
  }

  private async resolveCustomFieldsOrderedForWorkflow(
    workflowId: string
  ): Promise<CustomFieldViewModel[]> {
    const raw = this.customFieldsData.getFieldsByWorkflow(workflowId);
    let orderItems: WorkflowCardFieldDisplayOrderItem[] = [];
    try {
      orderItems = await this.cardFieldDisplayOrderApi.get(workflowId);
    } catch {
      // sem ordem remota: sortCustomFieldViewModelsByDisplayOrder usa `order` do schema
    }
    return sortCustomFieldViewModelsByDisplayOrder(raw, orderItems);
  }

  private mapCompanyUsersToOwnerOptions(): CardOwnerOptionModel[] {
    return this.companyUsers.users().map((u) => ({
      id: u.id,
      name: (u.name?.trim() || u.email?.trim() || u.id) as string,
      avatarUrl: u.avatarUrl?.trim() || null,
    }));
  }

  /** Evita GET /users repetido quando a lista da empresa já está em cache. */
  private async resolveOwnerOptions(): Promise<CardOwnerOptionModel[]> {
    await this.companyUsers.ensureLoaded();
    if (this.companyUsers.hasDataForActiveCompany()) {
      return this.mapCompanyUsersToOwnerOptions();
    }
    try {
      return await this.cardApi.getOwnerOptions();
    } catch {
      return [];
    }
  }

  private async loadCardBase(cardId: string): Promise<{ skippedFieldValuesReload: boolean }> {
    let skippedFieldValuesReload = false;
    try {
      const bundle = await this.cardApi.tryGetCardDetailBundle(cardId);
      if (bundle) {
        const baseCard = bundle.card;
        const wfId = baseCard.workflowId;
        const ownerOptions =
          bundle.ownerOptions.length > 0 ? bundle.ownerOptions : await this.resolveOwnerOptions();

        if (bundle.workflowFieldsRaw != null) {
          await this.customFieldsData.hydrateWorkflowFieldsFromDetailPayload(wfId, bundle.workflowFieldsRaw);
        } else {
          await this.customFieldsData.ensureWorkflowFieldsLoaded(wfId);
        }
        const customFields = await this.resolveCustomFieldsOrderedForWorkflow(wfId);

        let stageOptions = bundle.stageOptions;
        if (!stageOptions.length) {
          stageOptions = await this.cardApi.getCardStageOptions(wfId);
        }

        const ownerName =
          ownerOptions.find((owner) => owner.id === baseCard.ownerUserId)?.name ??
          this.i18n.t('common.unassigned');
        const createdByUserId = baseCard.createdByUserId?.trim() || null;
        const creatorOpt = createdByUserId
          ? ownerOptions.find((owner) => owner.id === createdByUserId)
          : undefined;
        const createdByName = createdByUserId
          ? creatorOpt?.name?.trim() ||
            this.i18n.t('cards.header.unknownCreator', undefined, 'Utilizador')
          : '';
        const createdByAvatarUrl = creatorOpt?.avatarUrl?.trim() || null;

        if (bundle.fieldValuesRaw != null) {
          this.fieldValuesSignal.set(this.fieldValuesApi.parseFieldValuesList(bundle.fieldValuesRaw, cardId));
          skippedFieldValuesReload = true;
        }

        this.cardSignal.set({
          id: baseCard.id,
          projectId: bundle.projectId,
          projectName: bundle.projectName,
          workflowId: baseCard.workflowId,
          workflowName: bundle.workflowName,
          workflowStageId: baseCard.workflowStageId,
          workflowStageName: resolveStageName(baseCard.workflowStageId, stageOptions, '-'),
          stageOptions,
          ownerOptions,
          title: baseCard.title,
          description: baseCard.description,
          code: baseCard.code,
          position: baseCard.position,
          isCompleted: baseCard.isCompleted,
          dueDate: baseCard.dueDate,
          ownerUserId: baseCard.ownerUserId,
          ownerName,
          createdByUserId,
          createdByName,
          createdByAvatarUrl,
          createdAt: baseCard.createdAt ?? '',
          updatedAt: baseCard.updatedAt ?? '',
          customFields,
          customFieldValues: [],
        });
        return { skippedFieldValuesReload };
      }

      const baseCard = await this.cardApi.getCardById(cardId);
      if (!baseCard) {
        this.cardSignal.set(null);
        this.errorCardSignal.set(this.i18n.t('cards.details.notFoundTitle'));
        return { skippedFieldValuesReload: false };
      }

      const ownerOptions = await this.resolveOwnerOptions();

      let projects = this.projectsStore.projects();
      if (projects.length === 0) {
        const raw = await this.operationsApi.getProjects();
        projects = raw.map(mapProjectContractToUiModel);
      }

      let workflows = this.workflowsStore.workflows();
      let workflow = workflows.find((item) => item.id === baseCard.workflowId);
      if (!workflow) {
        const rawWf = await this.operationsApi.getWorkflows();
        workflows = rawWf.map(mapWorkflowContractToUiModel);
        workflow = workflows.find((item) => item.id === baseCard.workflowId);
      }

      const wfId = baseCard.workflowId;

      const [stageOptions] = await Promise.all([
        workflow?.stages?.length
          ? Promise.resolve(
              [...workflow.stages]
                .sort((a, b) => a.order - b.order)
                .map((s) => ({ id: s.id, name: s.name }))
            )
          : this.cardApi.getCardStageOptions(wfId),
        this.customFieldsData.ensureWorkflowFieldsLoaded(wfId),
      ]);

      const projectId = workflow?.projectId ?? '';
      const projectName = projects.find((project) => project.id === projectId)?.name ?? '-';
      const ownerName =
        ownerOptions.find((owner) => owner.id === baseCard.ownerUserId)?.name ??
        this.i18n.t('common.unassigned');
      const createdByUserId = baseCard.createdByUserId?.trim() || null;
      const creatorOpt = createdByUserId
        ? ownerOptions.find((owner) => owner.id === createdByUserId)
        : undefined;
      const createdByName = createdByUserId
        ? creatorOpt?.name?.trim() ||
          this.i18n.t('cards.header.unknownCreator', undefined, 'Utilizador')
        : '';
      const createdByAvatarUrl = creatorOpt?.avatarUrl?.trim() || null;
      const customFields = await this.resolveCustomFieldsOrderedForWorkflow(wfId);

      this.cardSignal.set({
        id: baseCard.id,
        projectId,
        projectName,
        workflowId: baseCard.workflowId,
        workflowName: workflow?.name ?? '-',
        workflowStageId: baseCard.workflowStageId,
        workflowStageName: resolveStageName(baseCard.workflowStageId, stageOptions, '-'),
        stageOptions,
        ownerOptions,
        title: baseCard.title,
        description: baseCard.description,
        code: baseCard.code,
        position: baseCard.position,
        isCompleted: baseCard.isCompleted,
        dueDate: baseCard.dueDate,
        ownerUserId: baseCard.ownerUserId,
        ownerName,
        createdByUserId,
        createdByName,
        createdByAvatarUrl,
        createdAt: baseCard.createdAt ?? '',
        updatedAt: baseCard.updatedAt ?? '',
        customFields,
        customFieldValues: [],
      });
      return { skippedFieldValuesReload: false };
    } catch (error) {
      this.cardSignal.set(null);
      this.errorCardSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      return { skippedFieldValuesReload: false };
    }
  }

  private async reloadChecklists(): Promise<void> {
    const cardId = this.cardIdSignal();
    if (!cardId) return;
    this.loadingChecklistsSignal.set(true);
    this.errorChecklistsSignal.set(null);
    try {
      const response = await this.checklistsApi.getByCard(cardId);
      const currentById = new Map(this.checklists().map((item) => [item.id, item] as const));

      const withItems = await Promise.all(
        response.items.map(async (checklist) => {
          const vm = mapChecklistToViewModel(checklist);
          if (vm.items.length > 0) return vm;

          // API não retornou itens inline — busca separadamente
          const current = currentById.get(vm.id);
          try {
            const fetched = await this.checklistItemsApi.getByChecklist(cardId, vm.id);
            if (fetched.length > 0) return { ...vm, items: fetched };
          } catch {
            // fallback para itens locais se a busca separada falhar
          }
          return current ? { ...vm, items: current.items } : vm;
        })
      );

      this.checklistsSignal.set(withItems);
    } catch (error) {
      this.errorChecklistsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      this.checklistsSignal.set([]);
    } finally {
      this.loadingChecklistsSignal.set(false);
    }
  }

  private async reloadComments(): Promise<void> {
    const cardId = this.cardIdSignal();
    if (!cardId) return;
    this.loadingCommentsSignal.set(true);
    this.errorCommentsSignal.set(null);
    try {
      const [comments, owners] = await Promise.all([
        this.commentsApi.getByCard(cardId),
        this.resolveOwnerOptions(),
      ]);
      const ownersMap = new Map(owners.map((owner) => [owner.id, owner] as const));
      const mappedComments = comments.items.map((item) => mapCommentToViewModel(item, ownersMap));
      this.commentsSignal.set(mappedComments);
      const latest = [...mappedComments].sort((a, b) =>
        Date.parse(b.createdAt) - Date.parse(a.createdAt) || b.id.localeCompare(a.id))[0];
      this.workflowItems.updateLatestComment(cardId, latest?.content ?? null);
    } catch (error) {
      this.errorCommentsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      this.commentsSignal.set([]);
    } finally {
      this.loadingCommentsSignal.set(false);
    }
  }

  private async reloadAttachments(): Promise<void> {
    const cardId = this.cardIdSignal();
    if (!cardId) return;
    this.loadingAttachmentsSignal.set(true);
    this.errorAttachmentsSignal.set(null);
    try {
      const response = await this.attachmentsApi.getByCard(cardId);
      this.attachmentsSignal.set(response.items.map(mapAttachmentToViewModel));
    } catch (error) {
      this.errorAttachmentsSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      this.attachmentsSignal.set([]);
    } finally {
      this.loadingAttachmentsSignal.set(false);
    }
  }

  private async reloadActivities(): Promise<void> {
    const cardId = this.cardIdSignal();
    if (!cardId) return;
    this.loadingActivitiesSignal.set(true);
    this.errorActivitiesSignal.set(null);
    try {
      const response = await this.activitiesApi.getByCard(cardId);
      this.activitiesSignal.set(response.items.map(mapActivityToViewModel));
    } catch (error) {
      this.errorActivitiesSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      this.activitiesSignal.set([]);
    } finally {
      this.loadingActivitiesSignal.set(false);
    }
  }

  private async reloadFieldValues(): Promise<void> {
    const cardId = this.cardIdSignal();
    if (!cardId) return;
    this.loadingFieldValuesSignal.set(true);
    this.errorFieldValuesSignal.set(null);
    try {
      const response = await this.fieldValuesApi.getByCard(cardId);
      this.fieldValuesSignal.set(response.items);
    } catch (error) {
      this.errorFieldValuesSignal.set(this.resolveApiError(error, this.i18n.t('cards.details.errorLoadDetails')));
      this.fieldValuesSignal.set([]);
    } finally {
      this.loadingFieldValuesSignal.set(false);
    }
  }

  private resolveApiError(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const payload = this.asRecord(error.error);
      const message = this.asString(payload['message']).trim();
      if (message) return message;
      const errors = this.asArray(payload['errors'])
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean);
      if (errors.length > 0) return errors.join(' ');
      if (error.status === 403) return this.i18n.t('common.forbidden');
      if (error.status === 404) return this.i18n.t('common.notFound');
      if (error.status === 409) return this.i18n.t('common.conflict');
    }
    return fallback;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
  }

  private asNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private normalizeFieldId(value: string | null | undefined): string {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim().toLowerCase();
    if (!trimmed.length) return '';
    // Mesmo GUID pode vir com/sem chaves entre schema do workflow e GET dos valores do card.
    return trimmed.replace(/[{}]/g, '');
  }

  private async toFieldPersistedValue(
    cardId: string,
    fieldId: string,
    fieldType: CustomFieldType | undefined,
    value: CustomFieldRawValue
  ): Promise<UpsertCardFieldValueRequestModel['value']> {
    if (fieldType === 'BOOLEAN' || fieldType === 'CHECKBOX') {
      if (typeof value === 'boolean') return value ? 'true' : 'false';
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1') return 'true';
        if (normalized === 'false' || normalized === '0') return 'false';
      }
      return null;
    }

    if (fieldType !== 'FILE') {
      if (value instanceof File) return null;
      if (Array.isArray(value) && value.some((v) => v instanceof File)) return null;
      if (fieldType === 'PHONE' && typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const normalized = normalizeBrazilPhoneForSubmit(trimmed);
        if (/^\+[1-9]\d{6,14}$/.test(normalized)) {
          return normalized;
        }
        return trimmed.length ? trimmed : null;
      }
      if (fieldType === 'NUMBER' || fieldType === 'PROGRESS') {
        const schemaField = this.customFields().find((f) => f.fieldId === fieldId);
        const dp = resolveNumberFieldDecimalPlaces(schemaField, value);
        if (value == null) return null;
        if (typeof value === 'number' && Number.isFinite(value)) {
          return formatNumberFieldValueForApi(value, dp);
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed.length) return null;
          if (shouldPreserveLeadingZeroIntegerRaw(trimmed, dp)) {
            return trimmed.replace(/\s+/g, '');
          }
          const n = parseFlexibleNumberString(trimmed);
          if (n == null) return null;
          return formatNumberFieldValueForApi(n, dp);
        }
        return null;
      }
      return value as unknown as UpsertCardFieldValueRequestModel['value'];
    }

    const existingJson = this.resolveExistingFileFieldJson(fieldId);
    const existingAttachments = parseFileFieldValueToAttachments(existingJson ?? '');

    if (value instanceof File) {
      const uploaded = await this.attachmentsApi.upload(cardId, value);
      const piece = await this.descriptorFromUpload(cardId, uploaded);
      return this.serializeFileAttachmentsForApi([...existingAttachments, piece]);
    }

    if (Array.isArray(value) && value.length > 0 && value.every((v) => v instanceof File)) {
      const pieces: FileFieldAttachment[] = [];
      for (const f of value as File[]) {
        const uploaded = await this.attachmentsApi.upload(cardId, f);
        pieces.push(await this.descriptorFromUpload(cardId, uploaded));
      }
      return this.serializeFileAttachmentsForApi([...existingAttachments, ...pieces]);
    }

    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!this.isValidFileFieldStringValue(trimmed)) return null;
    const parsed = parseFileFieldValueToAttachments(trimmed);
    if (parsed.length > 0) {
      return this.serializeFileAttachmentsForApi(parsed);
    }
    return trimmed;
  }

  private resolveExistingFileFieldJson(fieldId: string): string | null {
    const normalized = this.normalizeFieldId(fieldId);
    const existing = this.fieldValuesSignal().find(
      (item) => this.normalizeFieldId(item.workflowFieldId) === normalized
    );
    const raw = existing?.value;
    if (typeof raw !== 'string') return null;
    const t = raw.trim();
    if (!t.length) return null;
    return parseFileFieldValueToAttachments(t).length > 0 ? t : null;
  }

  private async descriptorFromUpload(
    cardId: string,
    uploaded: CardAttachmentUploadResultModel
  ): Promise<FileFieldAttachment> {
    let storageKey = (uploaded.storageKey ?? '').trim();
    const urlCandidates = [uploaded.url, uploaded.assetUrl, uploaded.fileUrl, uploaded.path]
      .map((entry) => (entry ?? '').trim())
      .filter((entry) => entry.length > 0);
    const url = urlCandidates.find((entry) => this.isValidFileUrlOrPath(entry)) ?? '';
    const id = (uploaded.id ?? '').trim();
    const name =
      (uploaded.originalFileName || uploaded.fileName || '').trim() || undefined;
    const out: FileFieldAttachment = {};
    if (id) out.id = id;
    if (name) {
      out.fileName = name;
      out.originalFileName = name;
    }
    if (!storageKey && id) {
      const match = await this.findAttachmentFromServer(cardId, id);
      storageKey = match?.storageKey?.trim() || '';
      if (!out.fileName) {
        const recoveredName = (match?.originalFileName || match?.fileName || '').trim();
        if (recoveredName) {
          out.fileName = recoveredName;
          out.originalFileName = recoveredName;
        }
      }
    }
    if (storageKey) out.storageKey = storageKey;
    if (url) {
      out.url = url;
      out.assetUrl = url;
    } else if (id) {
      out.assetUrl = `/api/cards/${cardId}/attachments/${id}/download`;
    }
    return out;
  }

  private serializeFileAttachmentsForApi(entries: FileFieldAttachment[]): string | null {
    const compact = entries
      .map((entry) => {
        const fileName =
          entry.originalFileName?.trim() || entry.fileName?.trim() || '';
        const storageKey = entry.storageKey?.trim() || '';
        const assetUrl =
          entry.assetUrl?.trim() ||
          entry.url?.trim() ||
          entry.fileUrl?.trim() ||
          entry.path?.trim() ||
          '';
        const out: Record<string, string> = {};
        if (fileName) out['fileName'] = fileName;
        if (storageKey) out['storageKey'] = storageKey;
        if (assetUrl) out['assetUrl'] = assetUrl;
        return out;
      })
      .filter((item) => !!item['fileName'] && (!!item['storageKey'] || !!item['assetUrl']));
    if (compact.length === 0) return null;
    return compact.length === 1 ? JSON.stringify(compact[0]) : JSON.stringify(compact);
  }

  private isValidFileFieldStringValue(value: string): boolean {
    if (!value) return false;
    if (this.isValidFileUrlOrPath(value)) return true;
    if (value.startsWith('[') && value.endsWith(']')) {
      try {
        const arr = JSON.parse(value) as unknown;
        if (!Array.isArray(arr) || arr.length === 0) return false;
        return arr.every((entry) => this.isValidFileFieldObject(entry));
      } catch {
        return false;
      }
    }
    if (!(value.startsWith('{') && value.endsWith('}'))) return false;
    try {
      const parsed = JSON.parse(value) as unknown;
      return this.isValidFileFieldObject(parsed);
    } catch {
      return false;
    }
  }

  private isValidFileFieldObject(raw: unknown): boolean {
    if (!raw || typeof raw !== 'object') return false;
    const parsed = raw as Record<string, unknown>;
    const storageKey = typeof parsed['storageKey'] === 'string' ? parsed['storageKey'].trim() : '';
    const rawUrl = typeof parsed['url'] === 'string' ? parsed['url'].trim() : '';
    const rawAssetUrl = typeof parsed['assetUrl'] === 'string' ? parsed['assetUrl'].trim() : '';
    const rawFileUrl = typeof parsed['fileUrl'] === 'string' ? parsed['fileUrl'].trim() : '';
    const path = typeof parsed['path'] === 'string' ? parsed['path'].trim() : '';
    const id = typeof parsed['id'] === 'string' ? parsed['id'].trim() : '';
    const validUrl =
      [rawUrl, rawAssetUrl, rawFileUrl, path].find((u) => this.isValidFileUrlOrPath(u)) ?? '';
    return !!(id || storageKey || this.isValidFileUrlOrPath(validUrl));
  }

  private isValidFileUrlOrPath(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (normalized.startsWith('blob:') || normalized.startsWith('data:')) return false;
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) return true;
    if (normalized.startsWith('/api/cards/')) return true;
    return normalized.startsWith('/storage/');
  }

  private applyLocalFieldValue(fieldId: string, value: UpsertCardFieldValueRequestModel['value']): void {
    const normalized = this.normalizeFieldId(fieldId);
    if (!normalized) return;
    this.fieldValuesSignal.update((items) => {
      const next = [...items];
      const idx = next.findIndex((item) => this.normalizeFieldId(item.workflowFieldId) === normalized);
      if (value == null || (typeof value === 'string' && !value.trim())) {
        if (idx >= 0) next.splice(idx, 1);
        return next;
      }
      const persistedValue = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? value
        : String(value);
      if (idx >= 0) {
        next[idx] = { ...next[idx]!, value: persistedValue };
        return next;
      }
      const cardId = this.cardIdSignal() ?? '';
      next.push({
        id: '',
        cardId,
        workflowFieldId: fieldId,
        value: persistedValue,
      });
      return next;
    });
  }

  private async verifyFieldValuePersistence(
    cardId: string,
    fieldId: string,
    shouldExist: boolean
  ): Promise<boolean> {
    try {
      const response = await this.fieldValuesApi.getByCard(cardId);
      const normalizedTarget = this.normalizeFieldId(fieldId);
      const hit = response.items.find(
        (item) => this.normalizeFieldId(item.workflowFieldId) === normalizedTarget
      );
      if (!shouldExist) {
        return !hit;
      }
      if (!hit) return false;
      const raw = hit.value;
      if (raw == null) return false;
      if (typeof raw === 'string') return raw.trim().length > 0;
      if (Array.isArray(raw)) return raw.length > 0;
      return true;
    } catch {
      return false;
    }
  }

  private async verifyFieldValuePersistenceWithRetry(
    cardId: string,
    fieldId: string,
    shouldExist: boolean
  ): Promise<boolean> {
    if (await this.verifyFieldValuePersistence(cardId, fieldId, shouldExist)) {
      return true;
    }
    await new Promise<void>((resolve) => setTimeout(resolve, 260));
    return this.verifyFieldValuePersistence(cardId, fieldId, shouldExist);
  }

  private async findAttachmentFromServer(
    cardId: string,
    attachmentId: string
  ): Promise<{ fileName: string; originalFileName: string; storageKey: string } | null> {
    try {
      const response = await this.attachmentsApi.getByCard(cardId);
      const hit = response.items.find((item) => (item.id ?? '').trim() === attachmentId.trim());
      if (!hit) return null;
      return {
        fileName: (hit.fileName ?? '').trim(),
        originalFileName: (hit.originalFileName ?? '').trim(),
        storageKey: this.readAttachmentStorageKey(hit),
      };
    } catch {
      return null;
    }
  }

  private readAttachmentStorageKey(attachment: unknown): string {
    if (!attachment || typeof attachment !== 'object') return '';
    const row = attachment as Record<string, unknown>;
    const direct = typeof row['storageKey'] === 'string' ? row['storageKey'].trim() : '';
    if (direct) return direct;
    const alt = typeof row['key'] === 'string' ? row['key'].trim() : '';
    return alt;
  }

  private upsertChecklistItemLocal(checklistId: string, item: CardChecklistItemModel): void {
    this.checklistsSignal.update((current) =>
      current.map((checklist) => {
        if (checklist.id !== checklistId) return checklist;
        const existing = checklist.items.find((entry) => entry.id === item.id);
        const nextItem: CardChecklistItemModel = {
          id: item.id,
          cardChecklistId: item.cardChecklistId || checklistId,
          title: item.title || existing?.title || '',
          isCompleted: item.isCompleted,
          order: item.order > 0 ? item.order : existing?.order ?? checklist.items.length + 1,
          completedAt: item.completedAt ?? existing?.completedAt ?? null,
          completedByUserId: item.completedByUserId ?? existing?.completedByUserId ?? null,
        };
        if (!nextItem.id) return checklist;
        const withoutCurrent = checklist.items.filter((entry) => entry.id !== nextItem.id);
        return {
          ...checklist,
          items: [...withoutCurrent, nextItem].sort((a, b) => a.order - b.order),
        };
      })
    );
  }

  private removeChecklistItemLocal(checklistId: string, itemId: string): void {
    this.checklistsSignal.update((current) =>
      current.map((checklist) =>
        checklist.id === checklistId
          ? { ...checklist, items: checklist.items.filter((entry) => entry.id !== itemId) }
          : checklist
      )
    );
  }

}
